import { useQueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@rw/shared';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { LoadingState } from '../../components/states';
import { Button } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { useT } from '../../i18n';

/** Landing page after "Sign in with Strava": the session token arrives in the URL fragment. */
export function StravaAuthPage() {
  const navigate = useNavigate();
  const t = useT();
  const qc = useQueryClient();
  const [failed, setFailed] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
    // Remove the token from the address bar and history right away.
    window.history.replaceState(null, '', '/auth/strava');
    if (!token) {
      setFailed(true);
      return;
    }
    useAuth.setState({ token });
    api<MeResponse>('/auth/me')
      .then((me) => {
        qc.clear();
        useAuth.getState().setSession(token, me);
        navigate('/', { replace: true });
      })
      .catch(() => {
        useAuth.getState().clear();
        setFailed(true);
      });
  }, [navigate, qc]);

  if (!failed) return <LoadingState label={t('Signing in with Strava…')} />;
  return (
    <main className="grid min-h-full place-items-center p-6 text-center">
      <div>
        <div className="font-display text-2xl font-bold uppercase">{t('Strava sign-in failed')}</div>
        <p className="mt-2 text-sm text-ink-400">{t('Please try again.')}</p>
        <Button className="mt-5" variant="secondary" onClick={() => navigate('/login', { replace: true })}>
          {t('Back to sign in')}
        </Button>
      </div>
    </main>
  );
}
